import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { blogApi } from "../../services/api";

function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, published, draft
  const [filterCategory, setFilterCategory] = useState("all");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadPosts();
    extractCategories();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const response = await blogApi.getAll(false); // Get all posts including drafts
      setPosts(response.data || []);
    } catch (error) {
      console.error("Error loading blog posts:", error);
      // Fallback to mock data if API fails
      setPosts(getMockPosts());
    } finally {
      setLoading(false);
    }
  };

  const extractCategories = () => {
    const mockPosts = getMockPosts();
    const uniqueCategories = [
      ...new Set(mockPosts.map((post) => post.category)),
    ];
    setCategories(["all", ...uniqueCategories]);
  };

  const getMockPosts = () => {
    return [
      {
        id: 1,
        title: "5 Leadership Lessons from Startup Failures",
        slug: "5-leadership-lessons-startup-failures",
        excerpt: "What failed startups teach us about effective leadership...",
        category: "Leadership",
        author: "Mohammad Abdul Matin Emon",
        is_published: true,
        published_at: "2024-01-15T10:30:00Z",
        views: 1250,
        featured_image: null,
      },
      {
        id: 2,
        title: "Innovation in the Digital Age",
        slug: "innovation-digital-age",
        excerpt: "How to foster innovation in modern organizations...",
        category: "Growth",
        author: "Mohammad Abdul Matin Emon",
        is_published: true,
        published_at: "2024-01-10T14:20:00Z",
        views: 980,
        featured_image: null,
      },
      {
        id: 3,
        title: "Building a High-Performance Team",
        slug: "building-high-performance-team",
        excerpt: "Key strategies for assembling and leading effective teams...",
        category: "Leadership",
        author: "Mohammad Abdul Matin Emon",
        is_published: false,
        published_at: null,
        views: 0,
        featured_image: null,
      },
      {
        id: 4,
        title: "The Startup Ecosystem in Bangladesh",
        slug: "startup-ecosystem-bangladesh",
        excerpt: "Analysis of opportunities and challenges...",
        category: "Ecosystem",
        author: "Mohammad Abdul Matin Emon",
        is_published: true,
        published_at: "2023-12-20T09:15:00Z",
        views: 750,
        featured_image: null,
      },
      {
        id: 5,
        title: "From Idea to MVP: A Practical Guide",
        slug: "idea-to-mvp-practical-guide",
        excerpt: "Step-by-step approach to validating ideas...",
        category: "Startups",
        author: "Mohammad Abdul Matin Emon",
        is_published: true,
        published_at: "2023-12-15T11:45:00Z",
        views: 1200,
        featured_image: null,
      },
      {
        id: 6,
        title: "Scaling Your Business: Key Considerations",
        slug: "scaling-business-key-considerations",
        excerpt: "Critical factors to consider when planning...",
        category: "Growth",
        author: "Mohammad Abdul Matin Emon",
        is_published: false,
        published_at: null,
        views: 0,
        featured_image: null,
      },
    ];
  };

  const handleEdit = (postId) => {
    navigate(`/admin/blog/${postId}/edit`);
  };

  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    try {
      // Try API delete first
      if (postToDelete.id && typeof postToDelete.id === "number") {
        await blogApi.delete(postToDelete.id);
      }

      // Update local state
      setPosts(posts.filter((p) => p.id !== postToDelete.id));
      alert("Blog post deleted successfully!");
    } catch (error) {
      console.error("Error deleting post:", error);
      // Fallback to local state update
      setPosts(posts.filter((p) => p.id !== postToDelete.id));
      alert("Blog post removed from local state (API might be unavailable)");
    } finally {
      setShowDeleteModal(false);
      setPostToDelete(null);
    }
  };

  const togglePublishStatus = async (post) => {
    try {
      const updatedPost = {
        ...post,
        is_published: !post.is_published,
        published_at: !post.is_published ? new Date().toISOString() : null,
      };

      // Try API update
      if (post.id && typeof post.id === "number") {
        await blogApi.update(post.id, updatedPost);
      }

      // Update local state
      setPosts(posts.map((p) => (p.id === post.id ? updatedPost : p)));

      alert(
        `Post ${updatedPost.is_published ? "published" : "unpublished"} successfully!`,
      );
    } catch (error) {
      console.error("Error updating post status:", error);
      // Fallback to local state update
      setPosts(
        posts.map((p) =>
          p.id === post.id ? { ...p, is_published: !p.is_published } : p,
        ),
      );
      alert("Status updated locally (API might be unavailable)");
    }
  };

  const viewPost = (slug) => {
    window.open(`/insights/${slug}`, "_blank");
  };

  const duplicatePost = (post) => {
    const newPost = {
      ...post,
      id: Math.max(...posts.map((p) => p.id)) + 1,
      title: `${post.title} (Copy)`,
      slug: `${post.slug}-copy`,
      is_published: false,
      published_at: null,
      views: 0,
      created_at: new Date().toISOString(),
    };

    setPosts([newPost, ...posts]);
    alert("Post duplicated successfully!");
  };

  // Filter posts based on search and filters
  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "published" && post.is_published) ||
      (filterStatus === "draft" && !post.is_published);

    const matchesCategory =
      filterCategory === "all" || post.category === filterCategory;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "Not published";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (isPublished) => {
    if (isPublished) {
      return (
        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
          Published
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
        Draft
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const colors = {
      Leadership: "bg-blue-100 text-blue-800",
      Growth: "bg-green-100 text-green-800",
      Startups: "bg-purple-100 text-purple-800",
      Ecosystem: "bg-orange-100 text-orange-800",
    };

    const colorClass = colors[category] || "bg-gray-100 text-gray-800";

    return (
      <span
        className={`px-3 py-1 ${colorClass} text-xs font-semibold rounded-full`}
      >
        {category}
      </span>
    );
  };

  const stats = {
    total: posts.length,
    published: posts.filter((p) => p.is_published).length,
    draft: posts.filter((p) => !p.is_published).length,
    totalViews: posts.reduce((sum, post) => sum + (post.views || 0), 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Insights / Blog Management
          </h1>
          <p className="text-gray-600">
            Manage your blog posts, articles, and insights
          </p>
        </div>
        <Link
          to="/admin/blog/new"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <span>+</span>
          <span>New Post</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-2xl">📝</span>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Posts</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Published</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.published}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <span className="text-2xl">📋</span>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Drafts</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.draft}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <span className="text-2xl">👁️</span>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Views</p>
              <p className="text-2xl font-bold text-[#3B82F6]">
                {stats.totalViews.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Posts
            </label>
            <input
              type="text"
              placeholder="Search by title or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {categories
                .filter((cat) => cat !== "all")
                .map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus("published")}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
          >
            Show Published
          </button>
          <button
            onClick={() => setFilterStatus("draft")}
            className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
          >
            Show Drafts
          </button>
          <button
            onClick={() => {
              setSearchTerm("");
              setFilterStatus("all");
              setFilterCategory("all");
            }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Posts Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading posts...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Published
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPosts.length > 0 ? (
                  filteredPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {post.title}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {post.excerpt || "No excerpt"}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          /{post.slug}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getCategoryBadge(post.category || "Uncategorized")}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => togglePublishStatus(post)}
                          className="focus:outline-none"
                        >
                          {getStatusBadge(post.is_published)}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(post.published_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-gray-700 font-medium">
                            {post.views?.toLocaleString() || 0}
                          </span>
                          <span className="ml-1 text-gray-500 text-sm">
                            views
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleEdit(post.id)}
                            className="px-3 py-1 bg-blue-50 text-blue-600 rounded text-sm font-medium hover:bg-blue-100"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => viewPost(post.slug)}
                            className="px-3 py-1 bg-green-50 text-green-600 rounded text-sm font-medium hover:bg-green-100"
                          >
                            View
                          </button>
                          <button
                            onClick={() => duplicatePost(post)}
                            className="px-3 py-1 bg-[#EFF6FF] text-[#3B82F6] rounded text-sm font-medium hover:bg-[#DBEAFE]"
                          >
                            Duplicate
                          </button>
                          <button
                            onClick={() => handleDeleteClick(post)}
                            className="px-3 py-1 bg-red-50 text-red-600 rounded text-sm font-medium hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="text-gray-400 text-4xl mb-4">📝</div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">
                        No posts found
                      </h3>
                      <p className="text-gray-500">
                        {searchTerm
                          ? "Try adjusting your search"
                          : "Create your first blog post"}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="mt-4 text-blue-600 hover:text-blue-800"
                        >
                          Clear search
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Bulk Actions</h3>
        <div className="flex flex-wrap gap-4">
          <select className="px-4 py-2 border border-gray-300 rounded-lg">
            <option>Bulk Actions</option>
            <option>Publish Selected</option>
            <option>Move to Draft</option>
            <option>Delete Selected</option>
          </select>
          <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Apply
          </button>
          <button className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            Select All
          </button>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-800 mb-3">
          Tips for Managing Insights
        </h3>
        <ul className="space-y-2 text-sm text-blue-700">
          <li className="flex items-start">
            <span className="mr-2">📝</span>
            <span>Use clear, SEO-friendly titles and excerpts</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">🏷️</span>
            <span>Categorize posts to help readers find related content</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">✍️</span>
            <span>Save as draft to work on posts before publishing</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">🔍</span>
            <span>
              Use the duplicate feature to create similar posts faster
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">📊</span>
            <span>
              Monitor view counts to understand what content resonates
            </span>
          </li>
        </ul>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && postToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Delete Blog Post
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "
                <strong>{postToDelete.title}</strong>"? This action cannot be
                undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setPostToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Post
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Insights;
